import { Skeleton } from '@/components/ui/skeleton'

function TickerItemSkeleton() {
  return (
    <div className="station-card">
      <div className="flex flex-col gap-1.5 min-w-35">
        <div className="flex items-center gap-2">
          <Skeleton className="w-4 h-4 rounded-full" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-3 w-8" />
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="flex flex-col items-center gap-1 min-w-20">
        <Skeleton className="h-3 w-10" />
        <Skeleton className="h-7 w-14 font-mono" />
      </div>
      <div className="flex flex-col items-center gap-1 min-w-17.5">
        <Skeleton className="h-3 w-8" />
        <Skeleton className="h-5 w-12" />
      </div>
      <div className="flex flex-col items-center gap-1 min-w-17.5">
        <Skeleton className="h-3 w-8" />
        <Skeleton className="h-5 w-12" />
      </div>
      <div className="flex flex-col items-center gap-1 min-w-17.5">
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-5 w-12" />
      </div>
    </div>
  )
}

function StationCardSkeleton() {
  return (
    <div className="grid-flip-card">
      <div className="p-4 rounded-lg bg-card/50 border border-ice-blue/10 h-full flex flex-col justify-between">
        {/* Header: name + state */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="w-5 h-5 rounded-full" />
          </div>
          <Skeleton className="h-3 w-24 mb-3" />

          {/* Main metric */}
          <div className="flex items-baseline gap-2 mb-1">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-10 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>

          {/* Sub info line */}
          <Skeleton className="h-3 w-48 mb-3" />
        </div>

        {/* Mini chart area */}
        <div className="flex items-end gap-1 h-12">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
              <Skeleton
                className="w-full rounded-sm"
                style={{ height: `${Math.random() * 24 + 8}px` }}
              />
              <Skeleton className="h-2 w-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function DashboardSkeleton() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex flex-col min-h-screen">
        {/* Header skeleton */}
        <header className="border-b border-ice/20 bg-mountain-dark/50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-12 w-60 md:h-14 md:w-56" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 sm:w-24 rounded-md" />
                <Skeleton className="h-9 w-9 sm:w-32 rounded-md" />
              </div>
            </div>
          </div>
        </header>

        {/* Ticker tape skeleton */}
        <div className="border-b border-ice/20">
          <div className="ticker-container">
            <div className="ticker-track h-23">
              <TickerItemSkeleton />
              <TickerItemSkeleton />
              <TickerItemSkeleton />
            </div>
          </div>
        </div>

        {/* Stats panel skeleton */}
        <div className="bg-card/30 border-y border-ice/10">
          <div className="container mx-auto px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {/* Map button */}
              <div className="hidden md:flex items-center gap-3 py-3 px-6 rounded-lg bg-ice/10 border border-ice/20">
                <Skeleton className="w-8 h-8 rounded" />
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>

              {/* Search button */}
              <div className="flex items-center gap-3 py-3 px-6 rounded-lg bg-ice/10 border border-ice/20 min-h-20">
                <Skeleton className="w-8 h-8 rounded-full" />
                <Skeleton className="h-5 w-48" />
              </div>

              {/* Top gainers */}
              <div className="flex gap-3 py-3 px-4 rounded-lg bg-ice/10 border border-ice/20 min-h-21.25 overflow-hidden items-center">
                <div className="flex flex-col gap-1 flex-1 min-w-0 max-w-full">
                  <Skeleton className="h-3 w-16 mb-1 max-w-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-6 h-6 rounded" />
                    <div className="flex flex-col gap-1 max-w-full">
                      <Skeleton className="h-3 w-28 max-w-full" />
                      <Skeleton className="h-2 w-20 max-w-full" />
                    </div>
                  </div>
                </div>
                <Skeleton className="h-14 w-px shrink-0" />
                <div className="flex flex-col gap-1 flex-1 shrink min-w-0 max-w-full">
                  <Skeleton className="h-3 w-16 mb-1 max-w-full " />
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-6 h-6 rounded" />
                    <div className="flex flex-col gap-1 max-w-full">
                      <Skeleton className="h-3 w-28 max-w-full" />
                      <Skeleton className="h-2 w-20 max-w-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Station grid skeleton */}
        <main className="flex-1">
          <div className="container mx-auto px-4 py-6">
            {/* Filter bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Skeleton className="w-5 h-5" />
                <Skeleton className="h-5 w-28" />
              </div>
              <Skeleton className="h-9 w-40 rounded-md" />
            </div>

            {/* Grid cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <StationCardSkeleton />
              <StationCardSkeleton />
              <StationCardSkeleton />
              <StationCardSkeleton />
              <StationCardSkeleton />
              <StationCardSkeleton />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
